import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarCheck, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });

  useEffect(() => {
    if (!token) setError('Invalid reset link. Please request a new password reset.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (formData.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setLoading(true); setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: formData.password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(message); toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      <header className="border-b border-ink-200 bg-paper/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="reset-logo-link">
            <div className="w-7 h-7 bg-ink flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-paper" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Enota</span>
          </Link>
          <Link to="/login" className="text-sm text-ink-600 hover:text-ink flex items-center gap-1.5" data-testid="reset-back-login">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 grid-bg">
        <div className="w-full max-w-md">
          <div className="card-base p-8">
            <p className="eyebrow mb-3">PASSWORD RESET</p>
            <h1 className="h2 mb-1">{success ? 'All set.' : 'Set a new password.'}</h1>
            <p className="text-ink-600 text-sm mb-6">
              {success ? 'Your password has been updated. Sign in below.' : 'Pick something strong you have not used before.'}
            </p>

            {success ? (
              <div className="text-center py-2">
                <div className="w-14 h-14 border border-success/40 bg-success/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-7 h-7 text-success" />
                </div>
                <button className="btn btn-primary w-full" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
                  Go to sign in
                </button>
              </div>
            ) : error && !token ? (
              <div className="text-center py-2">
                <div className="w-14 h-14 border border-respondent/40 bg-respondent/10 flex items-center justify-center mx-auto mb-5">
                  <AlertCircle className="w-7 h-7 text-respondent" />
                </div>
                <p className="text-ink-600 text-sm mb-5">{error}</p>
                <button className="btn btn-primary w-full" onClick={() => navigate('/forgot-password')} data-testid="request-new-link-btn">
                  Request new reset link
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="p-3 border border-respondent/30 bg-respondent/5 text-respondent text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="password" className="eyebrow mb-2 block">New password</label>
                  <input
                    id="password" type="password" placeholder="••••••••" className="input-editorial"
                    value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required minLength={6} data-testid="reset-password-input"
                  />
                  <p className="text-[11px] text-ink-600 mt-1.5 font-mono">MIN. 6 CHARACTERS</p>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="eyebrow mb-2 block">Confirm new password</label>
                  <input
                    id="confirmPassword" type="password" placeholder="••••••••" className="input-editorial"
                    value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required data-testid="reset-confirm-password-input"
                  />
                </div>
                <button type="submit" className="btn btn-primary w-full" disabled={loading} data-testid="reset-submit-btn">
                  {loading ? 'Resetting…' : 'Reset password'}
                </button>
              </form>
            )}

            {!success && !error && (
              <p className="text-center text-sm text-ink-600 mt-6">
                Remember your password?{' '}
                <Link to="/login" className="text-respondent font-medium hover:underline">Sign in</Link>
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordPage;
